<template lang="pug">
section.cte-detail(v-loading="loading")
  template(v-if="cte")
    el-header
      el-page-header(@back="goBack" :content="`CT-e ${cte.number || ''} / Série ${cte.series || ''}`")

    el-tag(v-if="cte.cancelledAt" type="danger" size="medium").cancelled-tag
      | Cancelado em {{ formatDate(cte.cancelledAt) }}

    h3 Identificação

    el-descriptions(:column="2" border)
      el-descriptions-item(label="Chave de acesso")
        code.access-key {{ cte.accessKey }}
      el-descriptions-item(label="Emitido em") {{ formatDate(cte.issuedAt) }}
      el-descriptions-item(label="Número") {{ cte.number || '—' }}
      el-descriptions-item(label="Série") {{ cte.series || '—' }}
      el-descriptions-item(label="Status SEFAZ") {{ describeStatus(cte.statusCode) }}
      el-descriptions-item(label="Nº do protocolo") {{ cte.protocolNumber || '—' }}
      el-descriptions-item(label="Valor total") {{ formatCurrency(cte.totalValue) }}
      el-descriptions-item(label="Ambiente") {{ cte.environment }}

    h3 Classificação

    el-descriptions(:column="2" border)
      el-descriptions-item(label="Tipo de CT-e") {{ describeCteType(cte.cteType) }}
      el-descriptions-item(label="Tipo de serviço") {{ describeServiceType(cte.serviceType) }}
      el-descriptions-item(label="Modal") {{ describeModal(cte.modal) }}
      el-descriptions-item(label="CFOP") {{ cte.cfop || '—' }}
      el-descriptions-item(label="Tipo de emissão") {{ describeEmissionType(cte.emissionType) }}

    h3 Partes

    .parties-grid
      .party-card(v-for="party in parties" :key="party.key")
        .party-label
          | {{ party.label }}
          el-tag.party-role(v-if="party.role" size="mini" type="info") {{ party.role }}
        .party-name {{ party.name || '—' }}
        .party-doc(v-if="party.cnpj") {{ formatCnpj(party.cnpj) }}
        .party-doc(v-else-if="party.cpf") CPF: {{ formatCpf(party.cpf) }}
        .party-uf(v-if="party.uf") UF: {{ party.uf }}

    template(v-if="nfes.length || loadingNfes")
      h3 NFes transportadas
        el-tag.event-count(size="mini") {{ nfes.length }}

      small.section-hint
        | Documentos fiscais cuja mercadoria foi transportada por este CT-e (tag &lt;infDoc&gt;&lt;infNFe&gt;).

      el-empty(v-if="!nfes.length && !loadingNfes" description="Nenhuma NFe vinculada a este CT-e.")

      el-table(v-else :data="nfes" size="mini" v-loading="loadingNfes" style="width: 100%")
        el-table-column(label="Seq" width="80" align="right")
          template(slot-scope="scope") {{ scope.row.sequence }}
        el-table-column(label="Chave de acesso")
          template(slot-scope="scope")
            code.access-key {{ scope.row.nfeAccessKey }}

    h3 Armazenamento

    el-descriptions(:column="1" border)
      el-descriptions-item(label="NSU") {{ cte.nsu }}
      el-descriptions-item(label="Buscado em") {{ formatDate(cte.fetchedAt) }}
      el-descriptions-item(label="S3")
        code.s3-path s3://{{ cte.s3Bucket }}/{{ cte.s3Key }}

    h3 Eventos
      el-tag.event-count(size="mini") {{ cteEvents.length }}

    el-empty(v-if="!cteEvents.length && !loadingEvents" description="Nenhum evento registrado para este CT-e.")

    el-table(v-else :data="cteEvents" size="mini" v-loading="loadingEvents" style="width: 100%")
      el-table-column(label="Tipo" width="280")
        template(slot-scope="scope")
          div {{ describeEventType(scope.row.eventType) }}
          small.code {{ scope.row.eventType }} • seq {{ scope.row.sequence }}
      el-table-column(prop="occurredAt" label="Ocorrido em" width="160")
        template(slot-scope="scope") {{ formatDate(scope.row.occurredAt) }}
      el-table-column(prop="registeredAt" label="Registrado em" width="160")
        template(slot-scope="scope") {{ formatDate(scope.row.registeredAt) }}
      el-table-column(prop="protocolNumber" label="Protocolo" width="160")
      el-table-column(label="Status")
        template(slot-scope="scope")
          div {{ describeStatus(scope.row.statusCode) }}
          small.reason(v-if="scope.row.reason") {{ scope.row.reason }}

    //- MDF-e events live in their own section. They're operational metadata for the
    //- carrier's manifest (excluded from the accountant's export, see CTeService), so
    //- we render them after the fiscal events and only when present.
    template(v-if="mdfeEvents.length")
      h3 Eventos do MDF-e
        el-tag.event-count(size="mini" type="info") {{ mdfeEvents.length }}

      small.section-hint Eventos do manifesto de transporte. Não incluídos no ZIP de exportação.

      el-table(:data="mdfeEvents" size="mini" v-loading="loadingEvents" style="width: 100%")
        el-table-column(label="Tipo" width="280")
          template(slot-scope="scope")
            div {{ describeEventType(scope.row.eventType) }}
            small.code {{ scope.row.eventType }} • seq {{ scope.row.sequence }}
        el-table-column(prop="occurredAt" label="Ocorrido em" width="160")
          template(slot-scope="scope") {{ formatDate(scope.row.occurredAt) }}
        el-table-column(prop="registeredAt" label="Registrado em" width="160")
          template(slot-scope="scope") {{ formatDate(scope.row.registeredAt) }}
        el-table-column(prop="protocolNumber" label="Protocolo" width="160")
        el-table-column(label="Status")
          template(slot-scope="scope")
            div {{ describeStatus(scope.row.statusCode) }}
            small.reason(v-if="scope.row.reason") {{ scope.row.reason }}

  el-empty(v-else-if="!loading" description="CT-e não encontrado.")
</template>

<script>
import moment from 'moment';

import CteService from '@/services/fulfillment/CteService';

export default {
  name: 'cte-detail',
  props: {
    accessKey: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      loadingEvents: false,
      loadingNfes: false,
      cte: null,
      events: [],
      nfes: [],
    };
  },
  computed: {
    // Split the API response into the two tables the template renders. The backend
    // returns the full list (CT-e + MDF-e) — we slice client-side so a future filter
    // change on the server doesn't require a UI fix.
    cteEvents() {
      return this.events.filter(e => !CteService.isMdfeEvent(e.eventType));
    },
    mdfeEvents() {
      return this.events.filter(e => CteService.isMdfeEvent(e.eventType));
    },
    // Flatten the six SEFAZ parties into a uniform card list. Parties whose
    // name and doc are both empty are dropped — most CT-es leave dispatcher /
    // receiver / sender unpopulated when emit and dest already cover the
    // physical flow. The payer (tomador) carries an extra role badge derived
    // from `payerRole` so the reader knows WHICH party is paying.
    parties() {
      if (!this.cte) return [];
      const c = this.cte;
      const all = [
        {
          key: 'issuer',
          label: 'Emitente (transportadora)',
          name: c.issuerName, cnpj: c.issuerCnpj, cpf: null, uf: c.issuerUf,
        },
        {
          key: 'sender',
          label: 'Remetente',
          name: c.senderName, cnpj: c.senderCnpj, cpf: c.senderCpf, uf: c.senderUf,
        },
        {
          key: 'dispatcher',
          label: 'Expedidor',
          name: c.dispatcherName, cnpj: c.dispatcherCnpj, cpf: c.dispatcherCpf, uf: c.dispatcherUf,
        },
        {
          key: 'receiver',
          label: 'Recebedor',
          name: c.receiverName, cnpj: c.receiverCnpj, cpf: c.receiverCpf, uf: c.receiverUf,
        },
        {
          key: 'recipient',
          label: 'Destinatário',
          name: c.recipientName, cnpj: c.recipientCnpj, cpf: c.recipientCpf, uf: c.recipientUf,
        },
        {
          key: 'payer',
          label: 'Tomador',
          role: CteService.describePayerRole(c.payerRole),
          name: c.payerName, cnpj: c.payerCnpj, cpf: c.payerCpf, uf: c.payerUf,
        },
      ];
      // Drop empty parties: an entry survives only if at least the name OR a
      // doc (CNPJ/CPF) is present. Emitente and Destinatário are always
      // present in a valid CT-e, so they'll never be filtered out — the
      // others may legitimately be absent.
      return all.filter(p => p.name || p.cnpj || p.cpf);
    },
  },
  watch: {
    accessKey: {
      immediate: true,
      handler(newKey) {
        if (newKey) this.fetch(newKey);
      },
    },
  },
  methods: {
    async fetch(accessKey) {
      this.loading = true;
      try {
        this.cte = await CteService.findByAccessKey(accessKey);
        if (this.cte) {
          // Fire the two side-fetches in parallel — they don't depend on each
          // other and both feed the same page.
          this.loadEvents(accessKey);
          this.loadNfes(accessKey);
        }
      } catch (e) {
        if (e.status === 404) {
          this.cte = null;
        } else {
          this.$message.error('Falha ao carregar CT-e.');
        }
      } finally {
        this.loading = false;
      }
    },
    async loadEvents(accessKey) {
      this.loadingEvents = true;
      try {
        this.events = await CteService.findEvents(accessKey);
      } catch (e) {
        this.$message.error('Falha ao carregar eventos do CT-e.');
      } finally {
        this.loadingEvents = false;
      }
    },
    async loadNfes(accessKey) {
      this.loadingNfes = true;
      try {
        this.nfes = await CteService.findNfes(accessKey);
      } catch (e) {
        // NFe linkage is best-effort — don't block the page on this failing.
        this.$message.warning('Falha ao carregar NFes vinculadas ao CT-e.');
      } finally {
        this.loadingNfes = false;
      }
    },
    formatDate(iso) {
      if (!iso) return '—';
      return moment(iso).format('DD/MM/YYYY HH:mm');
    },
    formatCurrency(value) {
      if (value == null) return '—';
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    },
    formatCnpj(cnpj) {
      if (!cnpj || cnpj.length !== 14) return cnpj || '';
      return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
    },
    formatCpf(cpf) {
      if (!cpf || cpf.length !== 11) return cpf || '';
      return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
    },
    describeEventType(type) {
      return CteService.describeEventType(type);
    },
    describeStatus(code) {
      return CteService.describeStatusCode(code);
    },
    describeCteType(value) {
      return CteService.describeCteType(value);
    },
    describeServiceType(value) {
      return CteService.describeServiceType(value);
    },
    describeModal(value) {
      return CteService.describeModal(value);
    },
    describeEmissionType(value) {
      return CteService.describeEmissionType(value);
    },
    describePayerRole(value) {
      return CteService.describePayerRole(value);
    },
    goBack() {
      this.$router.push({ name: 'cte-list' });
    },
  },
};
</script>

<style lang="scss" scoped>
.cte-detail {
  h3 {
    margin-top: 24px;

    .event-count {
      margin-left: 8px;
      vertical-align: middle;
    }
  }

  .cancelled-tag {
    margin-top: 12px;
  }

  .access-key,
  .s3-path,
  .code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }

  .cnpj {
    color: #909399;
    font-size: 12px;
    display: block;
  }

  .reason {
    color: #909399;
    font-size: 11px;
    display: block;
  }

  .section-hint {
    color: #909399;
    display: block;
    margin: -8px 0 12px;
  }

  // Parties — a responsive grid of small "cards" so a CT-e with 4-6
  // populated parties doesn't crowd a 2-column el-descriptions and a CT-e
  // with just 2 (emit + dest) doesn't look sparse.
  .parties-grid {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    margin-top: 12px;

    .party-card {
      background: #fafafa;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      padding: 10px 12px;

      .party-label {
        align-items: center;
        color: #909399;
        display: flex;
        font-size: 11px;
        gap: 6px;
        letter-spacing: 0.3px;
        margin-bottom: 4px;
        text-transform: uppercase;

        .party-role {
          font-size: 10px;
          text-transform: none;
        }
      }

      .party-name {
        color: #303133;
        font-size: 14px;
        font-weight: 500;
      }

      .party-doc {
        color: #606266;
        font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
        font-size: 12px;
        margin-top: 2px;
      }

      .party-uf {
        color: #909399;
        font-size: 11px;
        margin-top: 2px;
      }
    }
  }
}
</style>
